<template>
  <div class="keys">
    <div v-if="!canCreate" class="dev-banner dev-banner--warn">
      <i class="el-icon-warning-outline"></i>
      <span>API access needs at least one machine with an active subscription. Subscribe a machine, then come back to create a key.</span>
    </div>

    <section class="dev-section">
      <div class="keys__head">
        <div>
          <h2 class="dev-section__title">API keys</h2>
          <div class="dev-section__sub">Use a key to read your machines, stock and sales from your own software or spreadsheets.</div>
        </div>
        <el-button type="primary" size="small" icon="el-icon-plus" :disabled="!canCreate" @click="openCreate">Create API key</el-button>
      </div>

      <div class="dev-card dev-card--flush">
        <el-table v-loading="loading" :data="visibleKeys" class="keys__table" empty-text="No API keys yet">
          <el-table-column label="Name" min-width="220">
            <template slot-scope="{ row }">
              <div class="keys__name" :class="{ 'is-off': !row.isActive }">{{ row.label }}</div>
              <span class="keys__type" :class="isConnector(row) ? 'keys__type--ai' : ''">{{ isConnector(row) ? "AI assistant" : "API key" }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Created" min-width="120">
            <template slot-scope="{ row }">{{ fmtDate(row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="Last used" min-width="120">
            <template slot-scope="{ row }">
              <span :title="row.lastUsedAt ? fmtDateTime(row.lastUsedAt) : ''">{{ row.lastUsedAt ? fromNow(row.lastUsedAt) : "Never" }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Calls this month" min-width="130" align="right">
            <template slot-scope="{ row }"><span class="vtm-num">{{ toCount(row.callsThisMonth).toLocaleString() }}</span></template>
          </el-table-column>
          <el-table-column label="Status" min-width="100">
            <template slot-scope="{ row }">
              <span class="vtm-pill" :class="row.isActive ? 'vtm-pill--ok' : 'vtm-pill--off'">{{ row.isActive ? "Active" : "Revoked" }}</span>
            </template>
          </el-table-column>
          <el-table-column label="" width="96" align="right">
            <template slot-scope="{ row }">
              <el-button v-if="row.isActive" type="text" class="keys__revoke" @click="revoke(row)">Revoke</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div v-if="keys.length" class="keys__foot">
          <el-checkbox v-if="revokedCount" v-model="showRevoked">Show {{ revokedCount }} revoked {{ revokedCount === 1 ? "key" : "keys" }}</el-checkbox>
          <span class="keys__total">
            Calls this month, all keys{{ revokedCount ? " (incl. revoked)" : "" }}:
            <b class="vtm-num">{{ monthTotal.toLocaleString() }}</b>
          </span>
        </div>
      </div>
    </section>

    <section class="dev-section">
      <div class="dev-card keys__tips">
        <div class="keys__tip"><i class="el-icon-lock"></i><span><b>Keep keys secret.</b> Anyone with a key can read your sales. Don't paste one into a website or share it in email.</span></div>
        <div class="keys__tip"><i class="el-icon-files"></i><span><b>One key per tool.</b> If you use the API in two places, give each its own key so you can revoke one without breaking the other.</span></div>
        <div class="keys__tip"><i class="el-icon-refresh"></i><span><b>Replacing a key?</b> Create the new one first, switch your tool over, then revoke the old one.</span></div>
      </div>
    </section>

    <!-- 新建密钥 -->
    <el-dialog :visible.sync="createOpen" :title="created ? 'Save your API key' : 'Create API key'" width="560px" append-to-body :close-on-click-modal="!created" :close-on-press-escape="!created" :show-close="!created" @closed="resetCreate">
      <template v-if="!created">
        <el-form @submit.native.prevent="create">
          <el-form-item label="Name">
            <el-input v-model="label" maxlength="60" :placeholder="'e.g. Inventory spreadsheet (blank = ' + defaultLabel() + ')'" autofocus></el-input>
            <div class="keys__hint">So you can tell your keys apart later.</div>
          </el-form-item>
        </el-form>
        <div class="keys__scope"><i class="el-icon-view"></i>Read-only: machines, inventory and orders on your account.</div>
      </template>
      <template v-else>
        <div class="dev-banner dev-banner--warn keys__once">
          <i class="el-icon-warning-outline"></i>
          <span>Copy this key now. For your security we only show it once — if you lose it, revoke it and create a new one.</span>
        </div>
        <copy-field :value="created.apiKey" label="API key"></copy-field>
        <div class="keys__try">Try it:</div>
        <code-tabs :samples="samples"></code-tabs>
      </template>
      <span slot="footer">
        <template v-if="!created">
          <el-button size="small" @click="createOpen = false">Cancel</el-button>
          <el-button size="small" type="primary" :loading="creating" @click="create">Create key</el-button>
        </template>
        <el-button v-else size="small" type="primary" @click="createOpen = false">I've saved my key</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  // API 密钥面板：新建（明文只显示一次）/ 列表 / 撤销
  import CopyField from "./CopyField";
  import CodeTabs from "./CodeTabs";
  import { codeSamples, isConnectorKey, parseServerDate, toCount } from "./developerContent";

  export default {
    name: "KeysPanel",
    components: { CopyField, CodeTabs },
    props: {
      access: { type: Object, required: true }
    },
    data() {
      return {
        keys: [],
        loading: false,
        showRevoked: false,
        createOpen: false,
        creating: false,
        label: "",
        created: null
      };
    },
    computed: {
      canCreate() {
        return this.access.staff || this.access.subscribed;
      },
      visibleKeys() {
        return this.showRevoked ? this.keys : this.keys.filter(k => k.isActive);
      },
      monthTotal() {
        return this.keys.reduce((sum, k) => sum + toCount(k.callsThisMonth), 0);
      },
      revokedCount() {
        return this.keys.filter(k => !k.isActive).length;
      },
      samples() {
        return codeSamples(this.access.apiBaseUrl, this.created && this.created.apiKey);
      }
    },
    created() {
      this.load();
    },
    methods: {
      load() {
        this.loading = true;
        this.$api
          .request(this.$url.ApiKey.list, this.$method.get)
          .then(res => {
            if (res && res.code === 0) {
              this.keys = res.data || [];
            }
          })
          .finally(() => (this.loading = false));
      },
      toCount,
      isConnector(row) {
        return isConnectorKey(row);
      },
      openCreate() {
        this.createOpen = true;
      },
      // 未填名称：取下一个未用过的「API key N」，避免重名
      defaultLabel() {
        const used = new Set(this.keys.map(k => String(k.label || "").toLowerCase()));
        let n = this.keys.length + 1;
        while (used.has("api key " + n)) {
          n++;
        }
        return "API key " + n;
      },
      resetCreate() {
        this.label = "";
        this.created = null;
      },
      create() {
        if (this.creating) {
          return;
        }
        this.creating = true;
        this.$api
          .request(this.$url.ApiKey.create, this.$method.post, { label: this.label.trim() || this.defaultLabel() })
          .then(res => {
            if (res && res.code === 0) {
              this.created = res.data;
              this.load();
              this.$emit("changed");
            } else if (res && res.code === 1) {
              // Refusals (no paid machine, jumped-in session, staff account) come back as code 1;
              // the other error codes are already shown by the request interceptor
              this.$message.error(res.msg || "Could not create the key");
            }
          })
          // network and HTTP errors are already shown by the request interceptor
          .catch(() => {})
          .finally(() => (this.creating = false));
      },
      revoke(row) {
        const what = this.isConnector(row)
          ? "Any AI assistant signed in with this key will lose access until it signs in again."
          : "Anything using this key will stop working straight away. This can't be undone.";
        this.$confirm(what, `Revoke “${row.label}”?`, {
          confirmButtonText: "Revoke",
          cancelButtonText: "Cancel",
          type: "warning",
          confirmButtonClass: "el-button--danger"
        })
          .then(() => this.$api.request(this.$url.ApiKey.revoke, this.$method.post, { id: row.id }))
          .then(res => {
            if (res && res.code === 0) {
              this.$message.success("Key revoked");
              this.load();
              this.$emit("changed");
            }
          })
          .catch(() => {});
      },
      fmtDate(value) {
        return value ? parseServerDate(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
      },
      fmtDateTime(value) {
        return parseServerDate(value).toLocaleString();
      },
      fromNow(value) {
        const secs = Math.max(0, (Date.now() - parseServerDate(value).getTime()) / 1000);
        if (secs < 60) return "Just now";
        if (secs < 3600) return Math.floor(secs / 60) + " min ago";
        if (secs < 86400) return Math.floor(secs / 3600) + " h ago";
        if (secs < 86400 * 30) return Math.floor(secs / 86400) + " days ago";
        return this.fmtDate(value);
      }
    }
  };
</script>

<style scoped lang="scss">
  .keys__head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  .keys__name {
    font-weight: 600;
    color: var(--vtm-ink);

    &.is-off {
      color: var(--vtm-ink-3);
      text-decoration: line-through;
    }
  }

  .keys__type {
    display: inline-block;
    margin-top: 3px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--vtm-ink-2);
    background: var(--vtm-well);
    border-radius: 4px;
    padding: 1px 6px;

    &--ai {
      color: var(--vtm-info);
      background: var(--vtm-info-soft);
    }
  }

  .keys__revoke {
    color: var(--vtm-crit) !important;
  }

  .keys__foot {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    padding: 10px 16px;
    border-top: 1px solid var(--vtm-line);
  }

  .keys__total {
    margin-left: auto;
    font-size: 12.5px;
    color: var(--vtm-ink-2);
  }

  .keys__tips {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 14px;
  }

  .keys__tip {
    display: flex;
    gap: 10px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--vtm-ink-2);

    i {
      font-size: 17px;
      color: var(--vtm-mint-ink);
      margin-top: 1px;
    }

    b {
      color: var(--vtm-ink);
    }
  }

  .keys__hint {
    font-size: 12px;
    color: var(--vtm-ink-3);
    line-height: 1.4;
    margin-top: 4px;
  }

  .keys__scope {
    font-size: 12.5px;
    color: var(--vtm-ink-2);

    i {
      margin-right: 6px;
    }
  }

  .keys__once {
    margin-bottom: 12px;
  }

  .keys__try {
    margin: 16px 0 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink);
  }
</style>
